import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import type { ChartConfig } from '../../../types';
import { CHART_STYLE, truncateLabel, formatAxisNumber, formatDataValue, analyzeDecimals } from './chartStyles';

interface BarChartViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  stacked?: boolean;
  grouped?: boolean;
  groupKeys?: string[];
}

/**
 * Bar chart (vertical) and stacked bar chart.
 * Used for Discrete=1 + Numeric=1 (Rule 3b) and Discrete=2 + Numeric=1 (Rule 3c).
 */
export function BarChartView({ data, config, stacked, grouped, groupKeys }: BarChartViewProps) {
  const xKey = config.xAxis ?? '';
  const yKeys: string[] = Array.isArray(config.yAxis) ? config.yAxis : [config.yAxis ?? ''];
  const yKey = yKeys[0] ?? '';
  const showLabels = data.length <= CHART_STYLE.dataLabelThreshold;
  const { hasDecimals, allSmall } = analyzeDecimals(data, yKey);

  // Grouped bar chart: multiple numeric columns side-by-side per category
  // (e.g. Port on x-axis, Revenue + TEU as separate bars)
  if (grouped && yKeys.length >= 2) {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={CHART_STYLE.margin}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.xAxis.tickFill }}
            angle={CHART_STYLE.xAxis.angle}
            textAnchor={CHART_STYLE.xAxis.textAnchor}
            height={CHART_STYLE.xAxis.height}
            interval={0}
            tickFormatter={(v: string) => truncateLabel(v)}
          />
          <YAxis
            tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.yAxis.tickFill }}
            width={CHART_STYLE.yAxis.width}
            tickFormatter={formatAxisNumber}
          />
          <Tooltip
            contentStyle={CHART_STYLE.tooltip}
            formatter={(value, name) => [formatDataValue(Number(value ?? 0), hasDecimals, allSmall), name]}
          />
          {yKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              name={key}
              fill={CHART_STYLE.colors[i % CHART_STYLE.colors.length]}
              radius={[3, 3, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // For stacked bars: pivot data by groupBy column
  if (stacked && config.groupBy && groupKeys && groupKeys.length > 0) {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={CHART_STYLE.margin}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.xAxis.tickFill }}
            angle={CHART_STYLE.xAxis.angle}
            textAnchor={CHART_STYLE.xAxis.textAnchor}
            height={CHART_STYLE.xAxis.height}
            interval={0}
            tickFormatter={(v: string) => truncateLabel(v)}
          />
          <YAxis
            tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.yAxis.tickFill }}
            width={CHART_STYLE.yAxis.width}
            tickFormatter={formatAxisNumber}
          />
          <Tooltip
            contentStyle={CHART_STYLE.tooltip}
            formatter={(value) => [formatDataValue(Number(value ?? 0), hasDecimals, allSmall), '']}
          />
          {groupKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="stack"
              fill={CHART_STYLE.colors[i % CHART_STYLE.colors.length]}
              radius={i === groupKeys.length - 1 ? [2, 2, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Standard bar chart
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={CHART_STYLE.margin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.xAxis.tickFill }}
          angle={CHART_STYLE.xAxis.angle}
          textAnchor={CHART_STYLE.xAxis.textAnchor}
          height={CHART_STYLE.xAxis.height}
          interval={0}
          tickFormatter={(v: string) => truncateLabel(v)}
        />
        <YAxis
          tick={{ fontSize: CHART_STYLE.fontSize.tick, fill: CHART_STYLE.yAxis.tickFill }}
          width={CHART_STYLE.yAxis.width}
          tickFormatter={formatAxisNumber}
        />
        <Tooltip
          contentStyle={CHART_STYLE.tooltip}
          formatter={(value) => [formatDataValue(Number(value ?? 0), hasDecimals, allSmall), yKey]}
          labelFormatter={(label, _payload) =>
            label != null ? String(label) : ''
          }
        />
        <Bar dataKey={yKey} radius={[3, 3, 0, 0]}>
          {data.map((_, index) => (
            <Cell key={index} fill={CHART_STYLE.colors[index % CHART_STYLE.colors.length]} />
          ))}
          {showLabels && (
            <LabelList
              dataKey={yKey}
              position="top"
              style={{ fontSize: CHART_STYLE.fontSize.dataLabel, fill: '#374151' }}
              formatter={(label: string | number | boolean | null | undefined) =>
                formatDataValue(Number(label ?? 0), hasDecimals, allSmall)
              }
            />
          )}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
